// Pure helpers for owner-controlled + subscriber-overridable field ordering.
//
// Owner order: the server delivers each contact field with an `order` index that
// reflects the owner's explicit field position at snapshot time (stable across
// Refresh). New fields the owner adds after subscription arrive at the BOTTOM.
//
// Subscriber override: a subscriber may locally rearrange a contact's fields. The
// override is stored ONLY in localStorage as an ordered array of field keys. Any
// field NOT in the saved override (e.g. a newly-arrived field) goes to the BOTTOM,
// in server (owner) order. A "Restore original order" action clears the override.

export interface Orderable {
  key: string;
  order?: number;
}

// sortByOwnerOrder returns a new array sorted by the owner's `order` index
// (ascending). Fields without an order fall back to their current relative
// position (stable sort), placed after ordered ones.
export function sortByOwnerOrder<T extends Orderable>(fields: T[]): T[] {
  return fields
    .map((f, i) => ({ f, i }))
    .sort((a, b) => {
      const ao = a.f.order ?? Number.MAX_SAFE_INTEGER;
      const bo = b.f.order ?? Number.MAX_SAFE_INTEGER;
      if (ao !== bo) return ao - bo;
      return a.i - b.i; // stable
    })
    .map((x) => x.f);
}

// applySavedOrder reorders `fields` using a saved subscriber override (an ordered
// array of field keys). Fields present in the override follow that order. Fields
// NOT in the override (newly-arrived, or override predates them) are appended at
// the BOTTOM in server/owner order. Unknown keys in the override are ignored.
export function applySavedOrder<T extends Orderable>(fields: T[], savedKeys: string[]): T[] {
  const byKey = new Map(fields.map((f) => [f.key, f]));
  const out: T[] = [];
  const used = new Set<string>();
  for (const k of savedKeys) {
    const f = byKey.get(k);
    if (f && !used.has(k)) { out.push(f); used.add(k); }
  }
  // Remaining (new / not-in-override) fields, in owner order, at the bottom.
  const remaining = sortByOwnerOrder(fields.filter((f) => !used.has(f.key)));
  return [...out, ...remaining];
}

// orderKeys extracts the ordered key list from a field array (for persisting an
// override after a drag reorder).
export function orderKeys(fields: Orderable[]): string[] {
  return fields.map((f) => f.key);
}
